import { useEffect, useId, useState } from "react";
import { bool, func, number, oneOfType, shape, string } from "prop-types";
import range from "lodash/range";

import useModalScrollbarOffset from "../../utilities/use-modal-scrollbar-offset";

import HtmlContent from "../../components/html-content/HtmlContent";
import Portal from "../../components/portal/Portal";
import ShoppingBenefitPoints from "./ShoppingBenefitPoints";

import { Button, Form, Modal, Typography } from "@uhc-tempo/components";
const { Select } = Form;
const { Content, Footer, Header, Title } = Modal;
const { H2 } = Typography;

export default function ShoppingSelectQuantity({
  benefit,
  disabled,
  handleSelect,
  inProgress,
  pointsRemaining,
  setViewSelectQuantity,
}) {
  const {
    id,
    maxQuantity,
    multipleDescription,
    multipleTitle,
    name,
    points = 0,
    qty: value = 0,
  } = benefit || {};

  const [quantity, setQuantity] = useState(value);
  const [initialPoints, setInitialPoints] = useState(value * points);

  const [modalOpen, toggleModalOpen] = useModalScrollbarOffset(false);
  const uid = useId();

  function handleClose() {
    setViewSelectQuantity(null);
  }

  function handleInput(event) {
    const { target: input } = event || {};
    const value = !!input ? parseInt(input.value, 10) : 0;

    setQuantity(value);
  }

  function handleSubmit(event) {
    event.preventDefault();
    handleSelect(id, name, quantity);
    handleClose();
  }

  function isDisabledOption(value) {
    const valuePoints = value * points;
    const isDisabled =
      (!initialPoints && valuePoints > pointsRemaining) ||
      (!!initialPoints && valuePoints > pointsRemaining + initialPoints);
    return isDisabled;
  }

  useEffect(() => {
    toggleModalOpen(!!id);
  }, [id, toggleModalOpen]);

  useEffect(() => {
    const pts = points || 0;
    const qty = value || 0;

    setQuantity(qty);
    setInitialPoints(qty * pts);
  }, [points, value]);

  return (
    <Portal id={`shopping-select-quantity-${uid}`}>
      {modalOpen && !!maxQuantity && !isNaN(maxQuantity) && maxQuantity > 0 && (
        <Modal
          contentId={`shopping-select-quantity-modal-${uid}`}
          handleClose={handleClose}
        >
          <Header className="ua-border-radius-top-sm ua-bg-supporting-one-opacity-ten ua-padding-md-bottom">
            <Title>How many of this perk do you want?</Title>
          </Header>
          <Form noValidate onSubmit={handleSubmit}>
            <Content className="ua-modal__content-full-width ua-padding-jumbo-bottom ua-padding-xxxlg-top">
              <div className="ua-grid ua-grid-gap-2-sm ua-grid-cols-12">
                <div className="ua-col-auto-span-12-sm ua-col-auto-span-3-md">
                  <ShoppingBenefitPoints points={points} starting />
                </div>
                <div className="ua-col-auto-span-12-sm ua-col-auto-span-6-md">
                  <div className="ua-display-flex ua-align-items-flex-start">
                    <div className="ua-overflow-hidden">
                      {!!multipleTitle && (
                        <H2
                          className="ua-margin-xsm-bottom ua-padding-sm-top"
                          headingStyle={6}
                        >
                          {multipleTitle}
                        </H2>
                      )}
                      {!!multipleDescription && (
                        <HtmlContent
                          paragraphBodyStyle={3}
                          paragraphMargin="ua-margin-sm-bottom"
                          rawHtml={multipleDescription}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="ua-col-auto-span-12-sm ua-col-auto-span-3-md">
                  <Select
                    aria-label="Perk quantity:"
                    containerProps={{
                      className:
                        "tds-form__select ua-display-block ua-width-100",
                    }}
                    name="perk_quantity"
                    onBlur={handleInput}
                    onChange={handleInput}
                    required
                    value={quantity}
                  >
                    {range(maxQuantity + 1).map((value, i) => (
                      <Select.Option
                        disabled={isDisabledOption(value)}
                        key={`points-quantity-${value}`}
                        value={value}
                      >
                        {value}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            </Content>
            <Footer className="ua-justify-content-flex-end">
              <Button
                buttonType={disabled ? "disabled" : "secondary-one"}
                className="tds-margin-md-right"
                disabled={disabled}
                onClick={handleClose}
                type="button"
              >
                Cancel
              </Button>
              <Button
                buttonType={disabled ? "disabled" : "primary-one"}
                disabled={disabled}
                loading={inProgress}
                type="submit"
              >
                {inProgress ? "Saving…" : "Save selection"}
              </Button>
            </Footer>
          </Form>
        </Modal>
      )}
    </Portal>
  );
}

ShoppingSelectQuantity.propTypes = {
  benefit: shape({
    id: oneOfType([number, string]),
    maxQuantity: number,
    multipleDescription: string,
    multipleTitle: string,
    value: number,
  }),
  disabled: bool,
  handleSelect: func.isRequired,
  inProgress: bool,
  pointsRemaining: number,
  setViewSelectQuantity: func.isRequired,
};
